import { Component, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { CustomListDropdown } from 'src/app/interfaces/custom-list/custom-list-dropdown.interface';
import { CompanyModel } from 'src/app/models/management/company.model';
import { CompanyService } from 'src/app/services/management/company.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-advanced-options-company',
  templateUrl: './advanced-options-company.component.html',
  styleUrls: ['./advanced-options-company.component.scss'],
  standalone: false
})
export class AdvancedOptionsCompanyComponent {

  @ViewChild('advancedOptions') advancedOptionsModalComponent?: CustomModalComponent | null = null;
  @ViewChild('articleForOrderSelection') articleSelectionModalComponent?: CustomModalComponent | null = null;

  companyContract: CompanyModel = new CompanyModel();
  companyId: number = 0;
  disabledForm: boolean = true;
  formCompany: FormGroup = new FormGroup({});
  listArticles: CustomListDropdown[] = [];
  listConceptInvoicing: CustomListDropdown[] = [];
  listCountry: CustomListDropdown[] = [];
  listCurrency: CustomListDropdown[] = [];
  permissionUpdatable: boolean = false;
  suscription: Subscription;
  swAdvanceInvoice: boolean = false;
  visibleAdvancedOptions: boolean = true;

  constructor(private companyService: CompanyService,
    public config: DynamicDialogConfig,
    private formBuilder: FormBuilder,
    private toastHandlerService: ToastHandlerService) {
    this.validateForm(new CompanyModel());
  }

  async ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.suscription = input$?.subscribe((inputs: any) => {
      this.companyContract = inputs.companyContract;
      this.permissionUpdatable = inputs.permissionUpdatable;
      this.disabledForm = inputs.disabledForm;
      this.swAdvanceInvoice = inputs.swAdvanceInvoice;
      this.listArticles = inputs.listArticles;
      this.listConceptInvoicing = inputs.listConceptInvoicing;
      this.listCurrency = inputs.listCurrency;
      this.listCountry = inputs.listCountry;
      this.validateForm(this.companyContract);
    });
  }

  ngOnDestroy() {
    this.suscription?.unsubscribe();
  }

  async send() {
    this.validateFormControls();
    if (this.formCompany.valid) {
      const modelCompany = this.formCompany.getRawValue() as CompanyModel;
      this.companyContract.ivaRate = modelCompany.ivaRate;
      this.companyContract.reteIvaRate = modelCompany.reteIvaRate;
      this.companyContract.articleIdFixedQuota = modelCompany.articleIdFixedQuota;
      this.companyContract.conceptInvoicingIdFixedQuota = modelCompany.conceptInvoicingIdFixedQuota;
      this.companyContract.conceptInvoicingIdSale = modelCompany.conceptInvoicingIdSale;
      this.companyContract.conceptInvoicingIdReplacement = modelCompany.conceptInvoicingIdReplacement;
      this.companyContract.countryId = modelCompany.countryId;
      this.companyContract.currencyId = modelCompany.currencyId;
      this.companyContract.invoiceDecimals = modelCompany.invoiceDecimals;
      this.companyContract.swSortByInvoicingCodeGroup = modelCompany.swSortByInvoicingCodeGroup;
      this.companyContract.articleIdDifferenceAdvanceInvoice = modelCompany.articleIdDifferenceAdvanceInvoice;
      try {
        const response = await firstValueFrom(this.companyService.updateCompany(this.companyContract));
        this.companyContract = response;
        this.validateForm(this.companyContract);
        this.toastHandlerService.showToast('success', `La compañía fue modificada exitosamente.`);
      } catch (error) {
        console.error('Error al actualizar la compañía.', error);
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      ivaRate: {
        required: 'La Tasa de IVA es obligatoria.',
        max: 'La Tasa de IVA no puede ser mayor a 100.'
      },
      reteIvaRate: {
        required: 'La Retención de IVA es obligatoria.',
        max: 'La Retención de IVA no puede ser mayor a 100.'
      },
      countryId: {
        required: 'Debe seleccionar un País.'
      },
      currencyId: {
        required: 'Debe seleccionar una Moneda.'
      },
      invoiceDecimals: {
        required: 'Debe ingresar los Decimales de Factura.'
      }
    };
    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formCompany.get(controlName);
      if (control && control.invalid && control.errors) {
        const messages = controlMessages[controlName];
        Object.keys(control.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  updateFormState() {
    const shouldDisable = !this.permissionUpdatable || this.disabledForm;
    if (shouldDisable) {
      this.formCompany.disable();
    } else {
      this.formCompany.enable();
    }
  }

  validateForm(company: CompanyModel) {
    this.formCompany = this.formBuilder.group({
      ivaRate: [company.ivaRate, [Validators.required, Validators.max(100)]],
      reteIvaRate: [company.reteIvaRate, [Validators.required, Validators.max(100)]],
      articleIdFixedQuota: [company.articleIdFixedQuota],
      conceptInvoicingIdFixedQuota: [company.conceptInvoicingIdFixedQuota],
      countryId: [company.countryId, [Validators.required]],
      currencyId: [company.currencyId, [Validators.required]],
      invoiceDecimals: [company.invoiceDecimals, [Validators.required]],
      swSortByInvoicingCodeGroup: [company.swSortByInvoicingCodeGroup],
      conceptInvoicingIdSale: [company.conceptInvoicingIdSale],
      conceptInvoicingIdReplacement: [company.conceptInvoicingIdReplacement],
      articleIdDifferenceAdvanceInvoice: [company.articleIdDifferenceAdvanceInvoice],
    });
    this.updateFormState();
  }

  validateFormControls() {
    Object.keys(this.formCompany.controls).forEach((key) => {
      const control = this.formCompany.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
